import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { LogLevel } from '@azure/msal-common';
import { StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import {
  AccountAccess,
  AccountMemberRole,
  ApiConfiguration,
  AuthGuard,
  AuthInterceptor,
  AuthInterceptorConfig,
  CelumAuthModule,
  SERVICE_TOKEN_INTERCEPTOR_CONFIG,
  ServiceTokenInterceptorConfig,
  tenantHrefFactory,
  TenantService,
  USER_SERVICE_CONFIG,
  UserServiceConfig
} from '@celum/authentication';
import { CelumSnackbarModule } from '@celum/common-components';
import { CelumPropertiesProvider } from '@celum/core';
import { formatsRouteProviders, ProcessService } from '@celum/formats/shared';
import { CelumSnackbarListModule } from '@celum/internal-components';
import { CustomTranslateLoader } from '@celum/ng2base';
import { AppErrorHandler } from '@celum/shared/util';

import { appRoutes } from './app.routes';

function userServiceConfigFactory(): UserServiceConfig {
  return {
    getAccountAccessFilter: (accountAccess: AccountAccess) => accountAccess.effectiveRole === AccountMemberRole.ACCOUNT_ADMIN
  };
}

function translationFactory(http: HttpClient): CustomTranslateLoader {
  return new CustomTranslateLoader(http);
}

function serviceTokenInterceptorFactory(tenantService: TenantService): ServiceTokenInterceptorConfig<AuthInterceptorConfig> {
  const getConfiguration = () => {
    const tenantIdForFormats = tenantService.getCurrentTenantId();

    const formatsAppServiceTokenRequest = {
      clientId: 'formats-app',
      orgId: tenantIdForFormats,
      b2cTokenRequest: true
    };

    const apiConfigurations: ApiConfiguration[] = [
      {
        apiUrls: [CelumPropertiesProvider.properties.appProperties.formats.apiUrl, CelumPropertiesProvider.properties.appProperties.formats.processApiUrl],
        tokenEndpointConfigs: [
          {
            serviceTokenRequestDto: formatsAppServiceTokenRequest
          }
        ],
        tenantIdHeader: tenantIdForFormats
      }
    ];

    return {
      apiConfigurations,
      passThrough403Error: true
    };
  };

  return {
    getInterceptorConfiguration: () => getConfiguration()
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(BrowserAnimationsModule),
    provideRouter(appRoutes),
    provideHttpClient(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: translationFactory,
          deps: [HttpClient]
        }
      })
    ),
    importProvidersFrom(
      CelumAuthModule.forRoot({
        logLevel: LogLevel.Error
      })
    ),
    importProvidersFrom(CelumSnackbarModule, CelumSnackbarListModule),
    importProvidersFrom(StoreModule.forRoot({})), // needed for user.service.ts
    AuthGuard,
    ...formatsRouteProviders('FORMATS_APP', { includeAssignmentsService: true }),
    ProcessService,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' }
    },
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: { color: 'primary' }
    },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { disableTooltipInteractivity: true }
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: AppErrorHandler
    },
    {
      provide: SERVICE_TOKEN_INTERCEPTOR_CONFIG,
      useFactory: serviceTokenInterceptorFactory,
      deps: [TenantService]
    },
    {
      provide: USER_SERVICE_CONFIG,
      useFactory: userServiceConfigFactory
    },
    {
      provide: APP_BASE_HREF,
      deps: [TenantService],
      useFactory: tenantHrefFactory
    }
  ]
};
